import { statusConfig, priorityConfig } from "@/mocks/ticketData";
import type { ZammadUserTicket } from "@/mocks/zammadTickets";

interface TicketCardProps {
  ticket: ZammadUserTicket;
  onClick: (ticket: ZammadUserTicket) => void;
}

function formatRelativeTime(dateStr: string): string {
  if (!dateStr) return "N/A";
  const ticketDate = new Date(dateStr);
  if (isNaN(ticketDate.getTime())) return "N/A";

  const now = new Date();
  const diffMs = now.getTime() - ticketDate.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays} días`;
  
  const day = ticketDate.getDate();
  const month = ticketDate.getMonth() + 1;
  const year = ticketDate.getFullYear();
  const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${day} ${months[month - 1]} ${year}`;
}

export default function TicketCard({ ticket, onClick }: TicketCardProps) {
  const s = statusConfig[ticket.status];
  const p = priorityConfig[ticket.priority];

  return (
    <button
      onClick={() => onClick(ticket)}
      className="w-full text-left rounded-xl bg-background-50 border border-background-200/80 p-4 cursor-pointer hover:border-accent-200 hover:bg-accent-50/20 transition-all group"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-xs font-mono text-foreground-500 bg-background-100 px-2 py-0.5 rounded-md flex-shrink-0">
            {ticket.id}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.bg} ${s.color} ${s.border} border`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.color.replace("text-", "bg-")}`}></span>
            {s.label}
          </span>
        </div>
        <span className={`text-[10px] font-semibold whitespace-nowrap ${p.color}`}>
          {p.label}
        </span>
      </div>

      <h4 className="text-sm font-semibold text-foreground-900 mb-1.5 group-hover:text-accent-700 transition-colors pr-2 line-clamp-2">
        {ticket.title}
      </h4>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500 mb-2">
        <span className="flex items-center gap-1">
          <i className="ri-price-tag-3-line text-[10px]"></i>
          {ticket.category}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-user-line text-[10px]"></i>
          {ticket.assignedTo}
        </span>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-background-200/50">
        <span className="text-[10px] text-foreground-400 flex items-center gap-1">
          <i className="ri-time-line"></i>
          {formatRelativeTime(ticket.updatedAt)}
        </span>
        {ticket.slaBreached && (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
            <i className="ri-alert-line text-[10px]"></i>
            SLA excedido
          </span>
        )}
        {ticket.satisfactionRating !== null && (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600">
            <i className="ri-star-fill text-[10px]"></i>
            {ticket.satisfactionRating}/5
          </span>
        )}
        <i className="ri-arrow-right-s-line text-foreground-300 text-sm opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"></i>
      </div>
    </button>
  );
}